import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Checkbox,
  Typography,
} from "@mui/material";
import { Button } from "@mui/material";

import { Link } from "components/html/Link";
import { ButtonProgress } from "components/utility/ButtonProgress";
import { FormErrors } from "components/utility/FormErrors";
import { VSpace } from "components/utility/VSpace";

import { i18n } from "services/i18nService";
import {
  updatePaymentMethod,
  getSingleton,
} from "services/sosInventoryService/sosApi";
import { isoToLocalDateTime } from "services/utility/dates";
import { handleProgramError } from "services/utility/errors";
import { formatDateTimeToDate } from "services/utility/formatting";
import { getAccountChargeTotal } from "services/utility/misc";

import { useErrors } from "hooks/useErrors";

import { CurrentCharges } from "views/AccountInformation/CurrentCharges";

import { openAlert } from "globalState/alertSlice";

import { addDays } from "date-fns";

export function PaymentMethod(props) {
  const { close, setAccountInformation, accountInformation } = props;

  const {
    expiration,
    plan: planData,
    annualPlan,
    isTrial,
  } = accountInformation || {};

  const [isLoading, setIsLoading] = useState(false);
  const [isComponentLoaded, setIsComponentLoaded] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { errors, setErrors } = useErrors();

  const stripe = useRef(null);
  const cardElement = useRef(null);

  const dispatch = useDispatch();

  useEffect(() => setIsComponentLoaded(true), []);

  useEffect(() => {
    stripe.current = window.Stripe(process.env.REACT_APP_STRIPE_TOKEN);
    const elements = stripe.current.elements();
    cardElement.current = elements.create("card", {
      hidePostalCode: true,
    });

    // check if the component is mounted and then mount the card element
    if (isComponentLoaded) {
      // card.mount("#card-element");
      cardElement.current.mount("#card-element");
    }
  }, [isComponentLoaded]);

  async function savePaymentMethod(e) {
    e.preventDefault();
    setIsLoading(true);
    const { token, error } = await stripe.current.createToken(
      cardElement.current
    );
    if (error) {
      handleProgramError(i18n("alert.ErrorSavingPaymentMethod"));
    } else {
      const { id, card } = token;
      const { success } = await updatePaymentMethod({
        token: id,
        cardType: card.brand,
        lastFour: card.last4,
      });
      if (success) {
        const message = i18n("alert.SavePaymentMethodSuccess");
        setAccountInformation(await getSingleton("company/accountInfo"));
        dispatch(openAlert({ type: "success", message }));
        close();
      } else {
        handleProgramError(i18n("alert.ErrorSavingPaymentMethod"));
      }
    }
    setIsLoading(false);
  }

  const term = annualPlan ? i18n("payment.year") : i18n("payment.month");
  const amount = getAccountChargeTotal(accountInformation);
  const trialEndDateObj = isoToLocalDateTime(expiration);
  const trialEnd = formatDateTimeToDate(trialEndDateObj);
  const chargeDate = formatDateTimeToDate(addDays(trialEndDateObj, 1));
  const plan = planData.name;

  return (
    <>
      <Dialog open={true} onClose={close}>
        <form id="payment-form" onSubmit={savePaymentMethod}>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h3">
                {i18n("payment.terms.title")}
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  backgroundColor: "grey.100",
                  borderRadius: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography>
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    {i18n("payment.terms.term.label")}{" "}
                  </Box>
                  {i18n(
                    annualPlan
                      ? "payment.terms.term.annual"
                      : "payment.terms.term.monthly"
                  )}
                </Typography>
                <Typography>
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    {i18n("payment.terms.refund.label")}{" "}
                  </Box>
                  {i18n("payment.terms.refund.body")}
                </Typography>
              </Box>
              <ul
                style={{
                  marginTop: "1rem",
                  paddingLeft: "1.5rem",
                  paddingRight: "1.5rem",
                }}
              >
                <Typography component="li">
                  {i18n("payment.terms.2", { term, amount, plan })}
                </Typography>
                <Box sx={{ ml: 2, mt: 0.5, mb: 0.5 }}>
                  <CurrentCharges accountInformation={accountInformation} />
                </Box>
                {isTrial && (
                  <Typography component="li">
                    {i18n("payment.terms.3", { trialEnd, chargeDate, amount })}
                  </Typography>
                )}
                <Typography component="li">
                  {i18n("payment.terms.4")}
                </Typography>
                <Typography component="li">
                  {i18n("payment.terms.5", { term })}
                </Typography>
              </ul>
            </Box>
            <Box sx={{ mb: 1.5, display: "flex", alignItems: "center" }}>
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
              <Typography variant="subheader">
                {i18n("payment.terms.agreeCheckbox.1")}{" "}
                <Link
                  component="a"
                  href="https://sosinventory.com/master-service-agreement/"
                  target="_blank"
                  rel="noreferrer"
                >
                  {i18n("trial.TermsOfService")}
                </Link>
                .
              </Typography>
            </Box>
            <FormErrors errors={errors} setErrors={setErrors} />
            <Box sx={{ display: termsAccepted ? "block" : "none" }}>
              <Typography variant="h4">
                {i18n("payment.EnterYourPaymentInformation")}
              </Typography>
              <VSpace space={1.5} />
              <div id="card-element" />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button color="secondary" onClick={close}>
              {i18n("global.Cancel")}
            </Button>
            <div style={{ position: "relative" }}>
              <Button
                color="secondary"
                disabled={isLoading || !termsAccepted}
                type="submit"
              >
                {i18n("payment.AddPayment")}
              </Button>
              {isLoading && <ButtonProgress color="secondary" />}
            </div>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
